import { ArcusMark, type ArcusMarkProps } from '@/components/icons/ArcusMark';
import { ArcusWordmark } from '@/components/icons/ArcusWordmark';
import { cn } from '@/utils/helpers/cn';

/**
 * A marca do Arcus nas suas três formas.
 *
 * | `variant`  | Em português | O que é                          | Arquivo de referência       |
 * | ---------- | ------------ | -------------------------------- | --------------------------- |
 * | `symbol`   | símbolo      | os anéis concêntricos, sozinhos  | `public/arcus-simbolo.svg`  |
 * | `wordmark` | logotipo     | a palavra ARCUS desenhada        | `public/arcus-logotipo.svg` |
 * | `lockup`   | assinatura   | símbolo acima, logotipo abaixo   | `public/arcus-assinatura.svg` |
 *
 * Os valores são em inglês porque `docs/02-convencoes-de-nomenclatura.md` exige
 * identificador em inglês; a coluna do meio é o de-para para conversa com design.
 *
 * ## Tamanho
 *
 * Sempre pela **largura** (`w-40`, `w-full`) — a altura sai da proporção de cada
 * variante. `size-40` funciona só em `symbol`, que é quadrado; nas outras duas
 * esmaga o desenho.
 */

/*
 * Proporções da assinatura, medidas em `public/arcus-assinatura.svg`: placa de
 * 274.87 de lado, vão de 18.03 até o topo do logotipo, logotipo de 270.9 de
 * largura — ou seja, praticamente a largura da placa.
 *
 * Aqui o logotipo recebe **exatamente** a largura do símbolo, e não os 98.6%
 * medidos: a diferença é resíduo de exportação (o vértice do "A" e a curva do
 * "S" não tocam a caixa), e alinhar as duas bordas é o que a assinatura quer
 * dizer. O vão vira percentual da largura para a proporção sobreviver a
 * qualquer tamanho de render.
 */
const LOCKUP_GAP_RATIO = 18.03 / 274.87;

export interface BrandProps {
  /** Forma da marca. `lockup` é a assinatura completa. */
  variant?: 'symbol' | 'wordmark' | 'lockup';
  /** Sobre qual superfície a marca vai. Decide a cor da lâmpada e do logotipo. */
  tone?: 'on-dark' | 'on-light';
  /** Placa arredondada atrás do símbolo. Ignorada em `wordmark`. */
  plate?: 'none' | 'navy';
  /** `sweep` gira os anéis do símbolo. Ignorado em `wordmark`. */
  motion?: 'still' | 'sweep';
  /** Ritmo da varredura — ver `ArcusMark`. */
  sweep?: ArcusMarkProps['sweep'];
  /** Ponta dos arcos do símbolo. Ignorada em `wordmark`. */
  caps?: ArcusMarkProps['caps'];
  /** Rótulo acessível. Sem ele a marca é decorativa. */
  title?: string;
  className?: string;
}

export function Brand({
  variant = 'lockup',
  tone = 'on-dark',
  plate = 'none',
  motion = 'still',
  sweep,
  caps,
  title,
  className,
}: BrandProps) {
  if (variant === 'wordmark') {
    return <ArcusWordmark tone={tone} title={title} className={className} />;
  }

  if (variant === 'symbol') {
    return (
      <ArcusMark
        tone={tone}
        plate={plate}
        motion={motion}
        sweep={sweep}
        caps={caps}
        title={title}
        className={className}
      />
    );
  }

  return (
    <div
      className={cn('flex flex-col items-center', className)}
      // O rótulo sobe para o wrapper: são dois SVGs, e anunciar "Arcus" duas
      // vezes seguidas é ruído. Os filhos ficam decorativos.
      {...(title ? { role: 'img', 'aria-label': title } : {})}
    >
      <ArcusMark
        tone={tone}
        plate={plate}
        motion={motion}
        sweep={sweep}
        caps={caps}
        className="w-full"
      />
      {/*
        Margem em porcentagem resolve contra a **largura** do container em CSS,
        que é exatamente a regra medida na assinatura. Por isso o vão acompanha
        a escala sem nenhum cálculo em JS.
      */}
      <ArcusWordmark
        tone={tone}
        className="w-full"
        style={{ marginTop: `${LOCKUP_GAP_RATIO * 100}%` }}
      />
    </div>
  );
}
